import {
  AGENCY_HIDDEN_MENU_KEYS,
  SALON_FEATURE_LABELS,
  SALON_FEATURE_MODULE_FIELD,
  type AgencyHiddenMenuKey,
  type SalonFeatureKey,
  type SalonModuleStates,
  type StoredSalonModule,
} from './salon-module-keys';

export interface SalonModuleSources {
  /** `Organization.disabledModules`: the stored OFF list. */
  disabledModules: readonly StoredSalonModule[];
  /** `TurnTrackerConfig.isEnabled`; a salon without a config row has Turns off (the column defaults to false). */
  turnTrackerEnabled: boolean | null | undefined;
  /** `Organization.pointOfSaleMode`: only HAYVEN_POS checks out in Hayven. */
  pointOfSaleMode: 'ALONGSIDE_POS' | 'HAYVEN_POS';
}

/** Merges the three places a feature's on/off lives into the one `modules` object every surface reads. */
export function resolveSalonModules({
  disabledModules,
  turnTrackerEnabled,
  pointOfSaleMode,
}: SalonModuleSources): SalonModuleStates {
  return {
    calendar: !disabledModules.includes('CALENDAR'),
    turns: turnTrackerEnabled === true,
    marketing: !disabledModules.includes('MARKETING'),
    reports: !disabledModules.includes('REPORTS'),
    sales: pointOfSaleMode === 'HAYVEN_POS',
  };
}

export function isSalonFeatureOn(modules: SalonModuleStates, feature: SalonFeatureKey): boolean {
  return modules[SALON_FEATURE_MODULE_FIELD[feature]];
}

/**
 * Hayven check-in needs somewhere for an arrival to land: the calendar (booked visits) or the turn board
 * (walk-ins). With both off, check-in is forced off and turning it on is refused.
 */
export function canUseHayvenCheckIn(modules: SalonModuleStates): boolean {
  return modules.calendar || modules.turns;
}

/**
 * The menus the agency hid, in menu order. Calendar, Turns and Sales are add-on / mode choices (the setup chip
 * names them), so they never count here.
 */
export function listSalonFeaturesOff(modules: SalonModuleStates): AgencyHiddenMenuKey[] {
  return AGENCY_HIDDEN_MENU_KEYS.filter((feature) => !isSalonFeatureOn(modules, feature));
}

/** "Marketing off", "Marketing, Reports off", or "" when the agency hid nothing (agency salon card summary). */
export function formatFeaturesOffSummary(modules: SalonModuleStates): string {
  const off = listSalonFeaturesOff(modules);
  if (off.length === 0) return '';
  return `${off.map((feature) => SALON_FEATURE_LABELS[feature].name).join(', ')} off`;
}
